import { AxiosError, AxiosResponse } from 'axios'
import { QueryFunctionContext } from '@tanstack/react-query'

import { GetResponse, WithResponse } from '../models/Response'
import { getInstance } from './instance'
import { IOBudgetDistributionList, IODetail } from '../models/IO'
import {
  getAdCategoriesKey,
  getAudiencesListKey,
  getCampaignAttachedCreativesInfoKey,
  getCarriersKey,
  getConversionsInfoKey,
  getCountriesListKey,
  getInventoryGroupDetailsKey,
  getIODetailKey,
  getPoliticalAdvertiserInfoKey,
  getPreBidSegmentsKey,
  getStatesListKey,
} from './QueryKeys'
import {
  AdvertiserInfo,
  Audience,
  ConversionData,
  CountriesList,
  CountryResponse,
  Creative,
  InventoryGroupData,
  PrebidAudienceSegmentData,
  PrebidProviderData,
  PublisherCategory,
  PublisherCategoryData,
  StateResponse,
  StatesList,
} from '../models/Campaign'

export const getIODetails = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getIODetailKey['keys']>>): Promise<
  IODetail | {}
> => {
  const ioParams = queryKey[0]?.ioParam
  try {
    const response: AxiosResponse<WithResponse<IODetail>> =
      await getInstance().get(`/v3/cmp/io/${ioParams?.id}`)

    return response.data.data as IODetail
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getIOBudgetDistributionMethod = async (): Promise<
  IOBudgetDistributionList | {}
> => {
  try {
    const response: AxiosResponse<WithResponse<IOBudgetDistributionList>> =
      await getInstance().get(`/v3/cmp/static/budget-distribution-method/list`)

    return response.data.data as IOBudgetDistributionList
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getCountriesList = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getCountriesListKey['keys']>
>): Promise<CountriesList[]> => {
  try {
    const response: AxiosResponse<WithResponse<CountryResponse>> =
      await getInstance().post(`/v3/master/segment/country`, {
        segmentIds: queryKey[0]?.segmentIds,
      })

    const countries = response.data?.data?.data || []
    return countries.map(country => ({
      label: country.name,
      value: country.id,
      shortName: country.abbreviation,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getStatesList = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getStatesListKey['keys']>>): Promise<
  StatesList[]
> => {
  try {
    const response: AxiosResponse<WithResponse<StateResponse>> =
      await getInstance().post(`/v3/master/segment/state`, {
        segmentIds: queryKey[0]?.segmentIds,
      })

    const data = response?.data?.data?.data || []
    return data.map(state => ({
      label: state.name,
      value: state.id,
      abbreviation: state.abbreviation,
      geojsonUrl: state.geojsonUrl,
      v2Id: state.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getCampaignAttachedCreativesInfo = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getCampaignAttachedCreativesInfoKey['keys']>
>) => {
  const campaignId = queryKey[0]?.creativesParam.campaignId
  try {
    const response: AxiosResponse<GetResponse<Creative[]>> =
      await getInstance().get('/v2/crt/creatives/details', {
        params: { campaignIds: campaignId },
      })
    return response.data.responseObject
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getAudiencesList = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getAudiencesListKey['keys']>>) => {
  try {
    const response: AxiosResponse<GetResponse<Audience[]>> =
      await getInstance().get('/v2/audience/list', {
        params: {
          owIds: queryKey[0]?.audienceListParam.owIds,
          audienceTypeIds: queryKey[0]?.audienceListParam.audienceTypeIds,
        },
      })
    return response.data.responseObject
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getConversionsInfo = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getConversionsInfoKey['keys']>
>): Promise<ConversionData> => {
  try {
    const response: AxiosResponse<WithResponse<ConversionData>> =
      await getInstance().get(`/v3/cmp/conversions/list`, {
        params: { conversionIds: queryKey[0]?.conversionIds },
      })

    return response?.data?.data as ConversionData
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getPublisherCategories = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getAdCategoriesKey['keys']>
>): Promise<PublisherCategory[]> => {
  try {
    const response: AxiosResponse<WithResponse<PublisherCategoryData>> =
      await getInstance().post(`/v3/master/adcategories`, {
        ...(queryKey[0]?.publisherCategoryIds?.length
          ? { ids: queryKey[0]?.publisherCategoryIds }
          : {}),
      })
    return response.data.data?.data as PublisherCategory[]
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getInventoryGroupDetails = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getInventoryGroupDetailsKey['keys']>
>) => {
  try {
    const response: AxiosResponse<GetResponse<InventoryGroupData>> =
      await getInstance().get('/v2/inv/group/list', {
        params: {
          groupIds: queryKey[0]?.inventoryGroupParam.groupIds,
          pageNo: queryKey[0]?.inventoryGroupParam.pageNo,
          includeContextualCount:
            queryKey[0]?.inventoryGroupParam.includeContextualCount,
        },
      })
    return response.data.responseObject.data
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getPreBidAudienceSegments = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getPreBidSegmentsKey['keys']>>) => {
  try {
    const response: AxiosResponse<WithResponse<PrebidAudienceSegmentData>> =
      await getInstance().post(`/v3/audience/prebid/details/search/list`, {
        prebidAudienceSegmentIdList: queryKey[0]?.prebidAudienceSegmentIdList,
      })
    return response.data.data
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getPreBidProviderDetails = async () => {
  try {
    const response: AxiosResponse<WithResponse<PrebidProviderData>> =
      await getInstance().get(`/v3/audience/prebid/provider/details/list`)

    return response?.data?.data as PrebidProviderData
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getDeviceTypes = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/device_types`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getManufacturers = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/manufacturers`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getOSTypes = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/device_os`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getNetworkTypes = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/network_types`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getTrafficTypes = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/source_traffic_types`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getExchanges = async () => {
  try {
    const response: AxiosResponse<{ name: string; id: number }[]> =
      await getInstance().get(`/exchanges`)
    return response.data.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getCarriers = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getCarriersKey['keys']>>) => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(
      `/v2/master/segment/device-carrier?segmentIds=${queryKey[0]?.carriers}`
    )
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getAgeList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/age`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getGenderList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/gender`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getEthnicitiesList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/ethnicity-group`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getLanguagesList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/language`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getInterestsList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/interest`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getIncomesList = async () => {
  try {
    const response: AxiosResponse<
      GetResponse<{ data: { name: string; id: number }[] }>
    > = await getInstance().get(`/v2/master/segment/income`)
    return response.data.responseObject.data?.map(item => ({
      id: item.id,
      label: item.name,
      value: item.id,
    }))
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}

export const getAdvertiserList = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getPoliticalAdvertiserInfoKey['keys']>
>) => {
  try {
    const response: AxiosResponse<WithResponse<{ data: AdvertiserInfo[] }>> =
      await getInstance().get(
        `/v3/ua/organization/advertisers/list?searchField=&pageNo=1&limit=&advertiserIds=${queryKey[0]?.politicalAdvertiserId}`
      )
    return response?.data?.data?.data as AdvertiserInfo[]
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response || e)
  }
}
